import { escapeHtml, mailTemplateSchema, mergeContextSchema, mergeTokens, } from './templates.js';
const TOKEN = /\{\{\s*([^{}]*?)\s*\}\}/g;
const KNOWN = new Set(mergeTokens);
function isKnown(name) {
    return KNOWN.has(name);
}
/** The tokens a string names, in order of appearance, unfiltered. */
function tokensIn(text) {
    return [...text.matchAll(TOKEN)].map((m) => m[1] ?? '');
}
/**
 * The value a token renders to in one of the two bodies, or null when the context has
 * nothing usable for it. `items` is the one structured token: a list in HTML, one line per
 * item in text. Everything else is a single value, escaped for HTML, verbatim for text.
 */
function valueFor(token, context, body) {
    if (token === 'items') {
        const items = context.items;
        if (items === undefined)
            return null;
        return body === 'html'
            ? `<ul>${items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`
            : items.map((i) => `- ${i}`).join('\n');
    }
    const raw = context[token];
    if (raw === undefined)
        return null;
    return body === 'html' ? escapeHtml(raw) : raw;
}
export function renderMail(template, context) {
    // Parsed, not trusted — a caller that assembled either by hand gets the schema's answer,
    // including the refusal of any key that is not in the vocabulary.
    const t = mailTemplateSchema.parse(template);
    const c = mergeContextSchema.parse(context);
    const unresolved = new Set();
    for (const name of [...tokensIn(t.subject), ...tokensIn(t.html), ...tokensIn(t.text)]) {
        if (!isKnown(name) || valueFor(name, c, 'text') === null)
            unresolved.add(name);
    }
    if (unresolved.size > 0) {
        // Default sort: code units. A locale comparison here would make the message depend on
        // the machine that rendered it.
        return { unresolved: [...unresolved].sort() };
    }
    const fill = (text, body) => text.replace(TOKEN, (_whole, name) => {
        // Every name was checked above; a null here would be a bug, not a blank.
        const value = isKnown(name) ? valueFor(name, c, body) : null;
        if (value === null)
            throw new Error(`unresolved token survived the check: ${name}`);
        return value;
    });
    return {
        // The subject is plain text in every mail client; it is never HTML.
        subject: fill(t.subject, 'text'),
        html: fill(t.html, 'html'),
        text: fill(t.text, 'text'),
    };
}
/** True when a render succeeded. */
export function isRendered(result) {
    return 'subject' in result;
}
//# sourceMappingURL=render.js.map